/**
 * The mailbox engine (v0.3 RFC §3) — Phaser-free. Predicate evaluation,
 * cadence-gated arrival scan (one per tick, min 10-tick gap), and atomic
 * fail-loud effect application on read. Letters are data (src/data/letters);
 * only INSTANCES persist in run.mailbox.
 */

import { LADDER, fireLadderSettle } from "@/machines/ladder";
import { AUTOMATOR_ACTIONS_BY_LEVEL, CARE_TIERS, EXPANSION, MAILBOX, type CareTierId } from "@/data/tunables";
import { LETTERS, type Effect, type Letter, type Predicate } from "@/data/letters";
import { expansionPriceMultiplier, requirementMultiplier } from "@/data/upgrades";
import { unlockCriminal } from "@/systems/criminal";
import {
  effectiveCapacity,
  frontierTier,
  patientsInTier,
  type GameState,
  type TierId,
} from "@/state/GameState";
import { ratchetHighWater } from "@/systems/actions";

export type MailEvent =
  | { type: "letterArrived"; letterId: string; title: string }
  | { type: "letterRead"; letterId: string }
  | { type: "teachControl"; control: "recruit" | "admit" | "treat" }
  | { type: "nameFacilityRequested" }
  | { type: "capacityRaised"; tier: CareTierId }
  | { type: "tierUnlocked"; tier: TierId }
  | { type: "purchasableOffered"; tier: TierId; price: number }
  | { type: "prestigeOffered" }
  | { type: "brokerUnlocked" }
  | { type: "criminalUnlocked" };

export function letterById(id: string): Letter {
  const letter = LETTERS.find((l) => l.id === id);
  if (!letter) {
    throw new Error(`letterById: unknown letter id "${id}"`);
  }
  return letter;
}

export function unreadCount(state: GameState): number {
  return state.run.mailbox.arrived.filter((i) => !i.read).length;
}

function hasArrived(state: GameState, id: string): boolean {
  return state.run.mailbox.arrived.some((i) => i.id === id);
}

/** The prestige-gated eligibility layer (§10.3): mark-at-run-start + order. */
export function expansionEligible(state: GameState, tier: Exclude<TierId, "detox">): boolean {
  const order: TierId[] = [...CARE_TIERS, "alumni"];
  const mark = state.run.markAtRunStart;
  const markIdx = mark === "none" ? -1 : order.indexOf(mark);
  const tierIdx = order.indexOf(tier);
  // eligible only for the ONE tier just past the starting mark…
  if (tierIdx !== markIdx + 1) return false;
  // …and never re-offered once open or already offered this run
  if (state.run.availableTiers.includes(tier)) return false;
  return true;
}

export function evalPredicate(state: GameState, p: Predicate): boolean {
  switch (p.type) {
    case "tick":
      return state.run.tick >= p.atLeast;
    case "queueCount":
      return state.run.queue.length >= p.atLeast;
    case "firstIssue":
      return state.run.patients.some((x) => x.state === "issue" || x.issueHistory > 0);
    case "anyDeathOrIssues":
      // cumulative counters survive patient removal (a departed patient's
      // issues/deaths still count toward the Incident letter).
      return state.run.deathsEver > 0 || state.run.issuesEver >= p.issuesAtLeast;
    case "patientsInTier":
      return patientsInTier(state, p.tier).length >= p.atLeast;
    case "funding":
      return state.run.funding >= p.atLeast;
    case "completionsAtFrontier": {
      const frontier = frontierTier(state);
      if (frontier === null) return false;
      // faster-climb prestige perks lower the required completions — but ONLY
      // for expansion offers (forExpansion); an unrelated letter like cbup that
      // also counts frontier completions must NOT be sped up by the perk.
      const factor = p.forExpansion ? requirementMultiplier(state.account) : 1;
      const need = Math.max(1, Math.ceil(p.atLeast * factor));
      return state.run.graduationsByTier[frontier] >= need;
    }
    case "expansionEligible":
      return expansionEligible(state, p.tier);
    case "frontierAtLeast": {
      const frontier = frontierTier(state);
      if (frontier === null) return false;
      return CARE_TIERS.indexOf(frontier) >= CARE_TIERS.indexOf(p.tier);
    }
    case "deathsPending":
      return state.run.deathsPending >= p.atLeast;
    case "everyTicks":
      return state.run.tick > 0 && state.run.tick % p.period === 0;
    case "criminalActive":
      return state.run.criminal.unlocked;
    case "all":
      return p.of.every((sub) => evalPredicate(state, sub));
    default: {
      const never: never = p;
      throw new Error(`evalPredicate: unknown predicate ${JSON.stringify(never)}`);
    }
  }
}

/**
 * Per-tick arrival scan (RULED cadence: one arrival per tick, minimum
 * MAILBOX.minGapTicks between arrivals; satisfied-during-gap letters simply
 * arrive when the gap expires). Table order = arrival priority.
 */
export function scanArrivals(state: GameState): MailEvent[] {
  const mb = state.run.mailbox;
  // Latch newly-satisfied triggers EVERY tick (even inside the gap) so a
  // predicate that lapses before its delivery slot is never lost (the ruled
  // "nothing is lost" cadence). Table order is preserved by the scan below.
  for (const letter of LETTERS) {
    if (hasArrived(state, letter.id)) continue;
    if (mb.pending.includes(letter.id)) continue;
    if (evalPredicate(state, letter.trigger)) mb.pending.push(letter.id);
  }
  if (state.run.tick - mb.lastArrivalTick < MAILBOX.minGapTicks) return [];
  // deliver the earliest-in-table pending letter (one per gap)
  for (const letter of LETTERS) {
    if (!mb.pending.includes(letter.id)) continue;
    if (hasArrived(state, letter.id)) {
      mb.pending = mb.pending.filter((p) => p !== letter.id);
      continue;
    }
    mb.arrived.push({ id: letter.id, arrivedTick: state.run.tick, read: false });
    mb.pending = mb.pending.filter((p) => p !== letter.id);
    mb.lastArrivalTick = state.run.tick;
    return [{ type: "letterArrived", letterId: letter.id, title: letter.title }];
  }
  return [];
}

function applyEffect(state: GameState, effect: Effect, events: MailEvent[]): void {
  switch (effect.type) {
    case "unlockTier": {
      if (state.run.availableTiers.includes(effect.tier)) {
        throw new Error(`applyEffect: tier "${effect.tier}" is already open`);
      }
      state.run.availableTiers.push(effect.tier);
      if (effect.tier !== "alumni") ratchetHighWater(state, effect.tier);
      events.push({ type: "tierUnlocked", tier: effect.tier });
      break;
    }
    case "makePurchasable": {
      if (state.run.availableTiers.includes(effect.tier)) {
        throw new Error(`applyEffect: tier "${effect.tier}" is already open — offer invalid`);
      }
      // faster-climb prestige perks discount the expansion price (floored).
      const price = Math.max(1, Math.round(effect.price * expansionPriceMultiplier(state.account)));
      state.run.purchasable = { tier: effect.tier, price };
      events.push({ type: "purchasableOffered", tier: effect.tier, price });
      break;
    }
    case "capacityBonus": {
      state.run.capacityBonus[effect.tier] += effect.amount;
      events.push({ type: "capacityRaised", tier: effect.tier });
      break;
    }
    case "capacityMultiplier": {
      for (const t of effect.tiers) {
        state.run.capacityMultiplier[t] *= effect.factor;
        events.push({ type: "capacityRaised", tier: t });
      }
      break;
    }
    case "grantAutomatorLevel": {
      const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
      const ts = state.run.tiers[effect.tier];
      ts.automatorLevel = Math.min(max, ts.automatorLevel + 1); // never exceed max
      break;
    }
    case "nameFacility":
      events.push({ type: "nameFacilityRequested" });
      break;
    case "teach":
      events.push({ type: "teachControl", control: effect.control });
      break;
    case "offerPrestige":
      state.run.prestigeOffered = true;
      events.push({ type: "prestigeOffered" });
      break;
    case "unlockBroker":
      state.run.brokerUnlocked = true;
      events.push({ type: "brokerUnlocked" });
      break;
    case "spendFundingPerDeath": {
      // Death-Letter claw-back cost scales with the batched death count; the
      // atomic read rolls this back if any later effect throws.
      const cost = effect.perDeath * state.run.deathsPending;
      if (state.run.funding < cost) {
        throw new Error(
          `applyEffect: this response costs $${cost} (${state.run.deathsPending} death(s)), have $${Math.floor(state.run.funding)}`,
        );
      }
      state.run.funding -= cost;
      break;
    }
    case "clawbackRepPerDeath":
      state.run.reputation += effect.perDeath * state.run.deathsPending;
      break;
    case "clawbackHeatPerDeath":
      state.run.heat = Math.max(0, state.run.heat - effect.perDeath * state.run.deathsPending);
      break;
    case "advanceCompliance": {
      // v0.10 M2: the free SETTLE advance rides the Ladder machine — at
      // max the final state refuses and the level holds (same clamp).
      const next = fireLadderSettle(LADDER.compliance(), state);
      if (next !== null) state.run.complianceOfficerLevel = next;
      break;
    }
    case "resolveDeaths":
      state.run.deathsPending = 0;
      break;
    case "unlockCriminal":
      unlockCriminal(state);
      events.push({ type: "criminalUnlocked" });
      break;
    case "deaSpike":
      state.run.dea += effect.amount;
      break;
    default: {
      const never: never = effect;
      throw new Error(`applyEffect: unknown effect ${JSON.stringify(never)}`);
    }
  }
}

/**
 * Read a letter: apply its effects atomically (fail-loud). A CHOICE letter
 * (v0.5 Death Letter) requires a valid `choiceIndex` and applies that branch's
 * effects instead of the letter's. A REPEATABLE letter is REMOVED on read (so
 * it can re-arrive when its trigger fires again) rather than marked read.
 */
export function readLetter(state: GameState, id: string, choiceIndex?: number): MailEvent[] {
  const letter = letterById(id); // unknown ids fail here, field-named
  const instance = state.run.mailbox.arrived.find((i) => i.id === id);
  if (!instance) {
    throw new Error(`readLetter: letter "${id}" has not arrived`);
  }
  if (instance.read) {
    throw new Error(`readLetter: letter "${id}" is already read`);
  }
  // Resolve which effects to apply: a choice branch, or the letter's own.
  let effects: Effect[];
  if (letter.choices) {
    if (choiceIndex === undefined || !Number.isInteger(choiceIndex) || choiceIndex < 0 || choiceIndex >= letter.choices.length) {
      throw new Error(`readLetter: letter "${id}" needs a choiceIndex in 0..${letter.choices.length - 1}, got ${choiceIndex}`);
    }
    effects = letter.choices[choiceIndex]!.effects;
  } else {
    if (choiceIndex !== undefined) {
      throw new Error(`readLetter: letter "${id}" has no choices, but choiceIndex ${choiceIndex} was given`);
    }
    effects = letter.effects;
  }
  const events: MailEvent[] = [{ type: "letterRead", letterId: id }];
  // Atomic: apply to a deep clone, commit by swapping in only on success.
  const draft: GameState = {
    account: JSON.parse(JSON.stringify(state.account)),
    run: JSON.parse(JSON.stringify(state.run)),
  };
  for (const effect of effects) {
    applyEffect(draft, effect, events); // throws → live state fully untouched
  }
  assertCapacitySane(draft); // a misconfigured letter set fails loud here
  if (letter.repeatable) {
    // remove the instance so the letter can re-arrive on its next trigger
    draft.run.mailbox.arrived = draft.run.mailbox.arrived.filter((i) => i.id !== id);
  } else {
    draft.run.mailbox.arrived.find((i) => i.id === id)!.read = true;
  }
  // commit BOTH halves in one shot (account carries the high-water ratchet)
  state.account = draft.account;
  state.run = draft.run;
  return events;
}

/** Slot layouts support 3×3; capacity letters must never exceed the stage. */
export function assertCapacitySane(state: GameState): void {
  for (const t of CARE_TIERS) {
    if (effectiveCapacity(state, t) > 9) {
      throw new Error(`capacity for ${t} exceeds the 9-slot stage — letter set misconfigured`);
    }
  }
}

export { EXPANSION };
